import CustomSelect from "components/custom-select/CustomSelect";
import ModalLayout from "components/modal/modal-layout/ModalLayout";
import { PRACTICE_SETTING } from "components/provider-intake/utility/providerInfo";
import { STATE_OPTIONS } from "constants/stateOptions";
import messages from "messages";
import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Col, Form, Row } from "react-bootstrap";
import postOrchidPro from "services/postOrchidPro";
import { AmplitudePageName, track } from "utils/amplitude";
import { filter_keys } from "utils/fn";
import styles from "./BillingAddressEditor.module.scss";

const AMPLITUDE_COMPONENT_NAME = "address-editor-modal";

const BillingAddressEditor = ({
  show,
  onHide,
  amplitudePageName,
  setData,
  currBillingAddress,
}: {
  show: boolean;
  onHide: () => void;
  amplitudePageName: AmplitudePageName;
  setData: (data: Partial<OrchidPro>) => void;
  currBillingAddress: {
    office_address_line1: string;
    office_address_line2: string;
    office_city: string;
    office_state: string;
    office_zipcode: string;
  } | null;
}) => {
  const [addressLine1, setAddressLine1] = useState(
    currBillingAddress?.office_address_line1 || "",
  );
  const [addressLine2, setAddressLine2] = useState(
    currBillingAddress?.office_address_line2 || "",
  );
  const [city, setCity] = useState(currBillingAddress?.office_city || "");
  const [state, setState] = useState(currBillingAddress?.office_state || "");
  const [zipCode, setZipCode] = useState(
    currBillingAddress?.office_zipcode || "",
  );
  const [validated, setValidated] = useState(false);
  const [error, setError] = useState<any>();

  const errorRef = useRef<HTMLDivElement>(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidated(true);

    const form = e.currentTarget;
    if (!form.checkValidity()) {
      setError("Please fill out all required fields correctly.");
      return;
    }

    const editedLocation = {
      office_address_line1: addressLine1,
      office_address_line2: addressLine2,
      office_city: city,
      office_state: state,
      office_zipcode: zipCode,
    };

    try {
      const nextOrchidPro = await postOrchidPro(editedLocation);

      setData(filter_keys(nextOrchidPro, PRACTICE_SETTING));

      onHide();
    } catch (error) {
      setError(error);
    }
  };

  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [error]);

  return (
    <ModalLayout
      showHeaderDivider
      size="md"
      show={show}
      onHide={onHide}
      Header={"Edit Business address"}
      bodyClass={styles["address-editor__body"]}
      footerClass={styles["address-editor__footer"]}
      Footer={
        <>
          <Button variant="outline-primary" className="mr-2" onClick={onHide}>
            {messages.cancel}
          </Button>
          <Button form="billing-address-form" variant="primary" type="submit">
            {messages.save}
          </Button>
        </>
      }
    >
      <div className={styles["address-editor__section-title"]}>
        Business Address
      </div>
      <div className={styles["address-editor__description"]}>
        Displayed on subscription invoices, client invoices, statements, and
        superbills. Choose from office locations or enter a different location.
      </div>
      <Form
        noValidate
        validated={validated}
        onSubmit={handleSubmit}
        id="billing-address-form"
      >
        {error && (
          <Alert variant="danger" ref={errorRef}>
            {error}
          </Alert>
        )}
        <Form.Group>
          <Form.Label className={styles["address-editor__form-label"]}>
            Street <span className="required">*</span>
          </Form.Label>
          <Form.Control
            type="text"
            placeholder="Enter Street"
            value={addressLine1}
            onChange={(e) => setAddressLine1(e.target.value)}
            required
          />
        </Form.Group>

        <Form.Group>
          <Form.Label className={styles["address-editor__form-label"]}>
            Address 2 (Optional)
          </Form.Label>
          <Form.Control
            type="text"
            placeholder="Apt, Suite, Floor, etc."
            value={addressLine2}
            onChange={(e) => setAddressLine2(e.target.value)}
          />
        </Form.Group>

        <Row>
          <Form.Group as={Col} md={4}>
            <Form.Label className={styles["address-editor__form-label"]}>
              City <span className="required">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter City"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              required
            />
          </Form.Group>

          <Form.Group as={Col} md={4}>
            <Form.Label className={styles["address-editor__form-label"]}>
              State <span className="required">*</span>
            </Form.Label>
            <CustomSelect
              id={"office_state"}
              name={"office_state"}
              options={STATE_OPTIONS}
              onChange={(el) => setState(el.value)}
              value={STATE_OPTIONS.find((option) => option.value === state)}
              placeholder={messages.select}
              onClick={() =>
                track(
                  amplitudePageName,
                  [AMPLITUDE_COMPONENT_NAME, "state-dropdown"],
                  "click",
                )
              }
            />
          </Form.Group>

          <Form.Group as={Col} md={4}>
            <Form.Label className={styles["address-editor__form-label"]}>
              Zip Code <span className="required">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter Zip Code"
              value={zipCode}
              onChange={(e) => setZipCode(e.target.value)}
              required
            />
          </Form.Group>
        </Row>
      </Form>
    </ModalLayout>
  );
};

export default BillingAddressEditor;
